import { CHARACTER_VISIBILITY, DEFAULT_CHAT_LANGUAGE, useTranslatedLanguage, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import { Button, ButtonSize, ButtonVariant, Text } from "@next-platform/client-common-spicy-ui";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import Trans from "@/components/Trans/Trans";
import { ROUTES, SPICYCHAT_USER_PERMISSIONS } from "@/constants";
import { getIsPartner } from "@/utils";

import { TranslationStatusModal } from "./TranslationStatusModal";
import { TranslationUpgradeModal } from "./TranslationUpgradeModal";
import { CharacterTranslationProps } from "./types";

export const CharacterTranslation = ({
  characterId,
  characterVisibility,
  characterLanguage,
  style = "profile",
  onTranslationSuccess,
  onTranslationError,
  dataTestId = "",
}: CharacterTranslationProps): React.ReactElement | null => {
  const { getTranslatedLanguage } = useTranslatedLanguage();
  const navigate = useNavigate();
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const characterLanguageLower = characterLanguage?.toLowerCase() || DEFAULT_CHAT_LANGUAGE;
  const { userChatLanguage, userChatLanguageName } = useUserSettings();
  const { permissions } = useUserInfo();

  // Check user permissions for translation
  const canTranslatePrivate = permissions?.includes(SPICYCHAT_USER_PERMISSIONS.character_translate_private);

  const canTranslatePublic = permissions?.includes(SPICYCHAT_USER_PERMISSIONS.character_translate_public) || canTranslatePrivate;

  // Determine if user can translate this specific character
  const isPublicCharacter = characterVisibility === CHARACTER_VISIBILITY.PUBLIC;
  const canTranslateThisCharacter = isPublicCharacter ? canTranslatePublic : canTranslatePrivate;
  const languageLabel = getTranslatedLanguage(userChatLanguageName);

  // Handle status modal open/close
  const handleStatusModalOpenChange = (isOpen: boolean) => {
    setIsStatusModalOpen(isOpen);
  };

  // Handle upgrade modal open/close
  const handleUpgradeModalOpenChange = (isOpen: boolean) => {
    setIsUpgradeModalOpen(isOpen);
  };

  // Handle translation button click
  const handleTranslationButtonClick = () => {
    if (canTranslateThisCharacter) {
      setIsStatusModalOpen(true);
    } else {
      setIsUpgradeModalOpen(true);
    }
  };

  // Handle upgrade click
  const handleUpgradeClick = () => {
    navigate(ROUTES.subscribe);
  };

  // Handle translation success
  const handleTranslationSuccess = () => {
    if (onTranslationSuccess) {
      onTranslationSuccess();
    }
  };

  // Handle translation error
  const handleTranslationError = (error?: string) => {
    if (onTranslationError) {
      onTranslationError(error);
    }
  };
  const isPartner = getIsPartner();
  if (isPartner || !userChatLanguage || userChatLanguage === DEFAULT_CHAT_LANGUAGE || characterLanguageLower === userChatLanguage) {
    return null;
  }
  const BUTTON_STYLES = {
    chat: {
      variant: ButtonVariant.SECONDARY,
      size: ButtonSize.SMALL,
      className: "w-full py-1 px-1 text-xs min-h-0 mt-1",
    },
    profile: {
      variant: ButtonVariant.INVERSE,
      size: ButtonSize.MEDIUM,
      className: "flex items-center justify-center text-center gap-1.5",
    },
    "profile-v2": {
      variant: ButtonVariant.SECONDARY,
      size: ButtonSize.SMALL,
      className: "",
    },
  };
  const buttonStyle = BUTTON_STYLES[style];
  return (
    <>
      {/* Translation Button */}
      <Button {...buttonStyle} onClick={handleTranslationButtonClick}>
        <Text as="span" className={style === "profile" ? undefined : "text-xs"}>
          <Trans i18nKey="chatbot:translateTo" values={{ language: languageLabel }} />
        </Text>
      </Button>

      {/* Translation Status Modal */}
      <TranslationStatusModal
        isOpen={isStatusModalOpen}
        onOpenChange={handleStatusModalOpenChange}
        characterLanguage={characterLanguageLower}
        characterId={characterId}
        characterVisibility={characterVisibility}
        onTranslationSuccess={handleTranslationSuccess}
        onTranslationError={handleTranslationError}
        dataTestId={dataTestId}
      />

      {/* Translation Upgrade Modal */}
      <TranslationUpgradeModal isOpen={isUpgradeModalOpen} onOpenChange={handleUpgradeModalOpenChange} onUpgradeClick={handleUpgradeClick} />
    </>
  );
};
